import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox, Badge,
  DataTable, EmptyState, Spinner, Layout, BlockStack, InlineStack, Pagination,
} from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
import { useFraudProtection } from '../../hooks/useFraudProtection';
import { IconBadge } from '../shared/IconBadge';

function reasonLabel(reason: string): string {
  switch (reason) {
    case 'below_minimum_order_amount':
      return 'Below minimum order amount';
    case 'shopify_risk_cancel':
      return "Shopify risk: recommends cancel";
    case 'shopify_risk_investigate':
      return 'Shopify risk: recommends investigate';
    default:
      return reason;
  }
}

export function FraudProtectionPanel() {
  const {
    settings, flaggedOrders, loading, saving, error, total, page, lastPage,
    fetchSettings, fetchFlaggedOrders, saveSettings, setError,
  } = useFraudProtection();

  const [form, setForm] = useState<{ enabled: boolean; min_order_amount_cents: number } | null>(null);
  const [minAmountDollars, setMinAmountDollars] = useState('0');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchFlaggedOrders(1);
  }, [fetchSettings, fetchFlaggedOrders]);

  useEffect(() => {
    if (settings) {
      setForm(settings);
      setMinAmountDollars((settings.min_order_amount_cents / 100).toFixed(2));
    }
  }, [settings]);

  if (loading || !form) {
    return (
      <Page fullWidth title="Fraud & Bot-Order Protection">
        <Layout>
          <Layout.Section>
            {loading ? (
              <InlineStack align="center"><Spinner /></InlineStack>
            ) : (
              <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
                <p>{error ?? 'Failed to load fraud protection settings.'}</p>
              </Banner>
            )}
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const handleSave = async () => {
    setSaved(false);
    const cents = Math.round((Number(minAmountDollars) || 0) * 100);
    const result = await saveSettings({ ...form, min_order_amount_cents: cents });
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Fraud & Bot-Order Protection"
      subtitle="Flags orders that look like bot/card-testing activity so you can review them — never cancels or holds an order automatically"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Settings saved. New orders will be evaluated going forward.</p>
              </Banner>
            )}

            <Banner tone="info">
              <p>
                GrowthPilot AI only <strong>detects and notifies</strong> — it reads Shopify's own order risk
                assessment and your minimum-order-amount threshold, logs a flagged order, and emails/notifies you.
                It never cancels, holds, or refunds an order automatically. You always review and act in Shopify
                Admin.
              </p>
            </Banner>

            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                  <InlineStack gap="300" blockAlign="center" wrap={false}>
                    <IconBadge icon={AlertTriangleIcon} color="orange" />
                    <Text as="h2" variant="headingMd">Settings</Text>
                  </InlineStack>
                  <Badge tone={form.enabled ? 'success' : undefined}>{form.enabled ? 'Enabled' : 'Disabled'}</Badge>
                </InlineStack>

                <Checkbox
                  label="Flag possibly risky orders for review"
                  helpText="Off by default — turn this on to start evaluating new orders as they come in"
                  checked={form.enabled}
                  onChange={(v) => setForm({ ...form, enabled: v })}
                />

                <TextField
                  label="Minimum order amount"
                  type="number"
                  prefix="$"
                  autoComplete="off"
                  helpText="Orders below this amount are flagged (0 disables this specific check — Shopify's own risk signal still applies)"
                  value={minAmountDollars}
                  onChange={setMinAmountDollars}
                  min={0}
                />

                <Button variant="primary" loading={saving} onClick={handleSave}>
                  Save Settings
                </Button>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Flagged Orders ({total})</Text>

                {flaggedOrders.length === 0 ? (
                  <EmptyState
                    heading="No orders flagged yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Once enabled, orders that trip your minimum amount or Shopify's own risk signal will appear here.</p>
                  </EmptyState>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'text']}
                    headings={['Order', 'Total', 'Reasons']}
                    rows={flaggedOrders.map((o) => [
                      o.order_number ?? `#${o.shopify_order_id}`,
                      `$${o.total_price.toLocaleString()}`,
                      o.reasons.map(reasonLabel).join(', '),
                    ])}
                  />
                )}

                {lastPage > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={page > 1}
                      onPrevious={() => fetchFlaggedOrders(page - 1)}
                      hasNext={page < lastPage}
                      onNext={() => fetchFlaggedOrders(page + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
